import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import WhatsAppButton from "./WhatsAppButton";

function formatTanggalIndonesia(dateString) {
  if (!dateString) return "Tanggal belum tersedia";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Tanggal belum tersedia";

  return date.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export default function NewsDetail({ isAdmin, AdminSection }) {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [news, setNews] = useState(null);
  const [allNews, setAllNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNewsDetail = async () => {
      try {
        setLoading(true);

        const [detailRes, listRes] = await Promise.all([
          fetch(
            `https://bum-desa-warga-dekat-bandung-production.up.railway.app/api/news/${slug}`,
          ),
          fetch(
            "https://bum-desa-warga-dekat-bandung-production.up.railway.app/api/news",
          ),
        ]);

        const detailData = await detailRes.json();
        const listData = await listRes.json();

        if (!detailRes.ok) {
          throw new Error(detailData.error || "Gagal mengambil detail berita");
        }

        setNews(detailData);

        let newsArray = [];
        if (Array.isArray(listData)) {
          newsArray = listData;
        } else if (Array.isArray(listData.data)) {
          newsArray = listData.data;
        } else if (Array.isArray(listData.news)) {
          newsArray = listData.news;
        }

        setAllNews(newsArray);
      } catch (err) {
        console.error("Gagal ambil detail berita:", err);
        setNews(null);
        setAllNews([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNewsDetail();
  }, [slug]);

  const relatedNews = Array.isArray(allNews)
    ? allNews
        .filter(
          (item) =>
            String(item.id) !== String(news?.id) &&
            String(item.slug) !== String(news?.slug),
        )
        .slice(0, 3)
    : [];

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    alert("Link telah disalin!");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white font-serif">
        <Navbar />
        <div className="pt-32 text-center text-gray-500">Loading berita...</div>
        <Footer />
      </div>
    );
  }

  if (!news) {
    return (
      <div className="min-h-screen bg-white font-serif">
        <Navbar />
        <div className="pt-32 text-center px-6">
          <h1 className="text-2xl text-[#3d4f45] mb-4">
            Berita tidak ditemukan
          </h1>
          <button
            onClick={() => navigate("/news")}
            className="bg-[#3d4f45] text-white px-6 py-3 rounded-full text-sm hover:bg-[#4a5a50] transition"
          >
            Kembali ke Berita
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-serif">
      <style>{`
        .card-wrap-hover .card-img { transition:transform .45s ease; }
        .card-wrap-hover:hover .card-img { transform:scale(1.05); }
        .article-body p { margin-bottom: 1.2rem; color:#4b5563; font-size:.875rem; line-height:1.85; }
      `}</style>

      <Navbar />
      <WhatsAppButton />

      <section
        className="relative flex items-end"
        style={{
          height: "70vh",
          minHeight: "480px",
          backgroundImage: news.cover_url ? `url(${news.cover_url})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundColor: "#b8c5ba",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to top,rgba(10,25,16,0.88) 30%,rgba(0,0,0,0.25) 100%)",
          }}
        />

        <div className="relative z-10 w-full max-w-4xl mx-auto px-6 pb-12">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="text-xs bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full border border-white/30">
              Berita
            </span>
            <span className="text-xs text-white/70">
              {formatTanggalIndonesia(news.published_at)}
            </span>
          </div>

          <h1
            className="text-2xl md:text-4xl text-white font-light leading-snug mb-4"
            style={{ maxWidth: "720px" }}
          >
            {news.title || "Tanpa Judul"}
          </h1>

          <p className="text-white/70 text-sm">Admin BUMDes</p>
        </div>
      </section>

      <section className="py-4 px-6 bg-[#f5f7f6]">
        <div className="max-w-4xl mx-auto flex items-center gap-2 text-xs text-gray-500">
          <Link to="/" className="hover:text-[#3d4f45]">
            Home
          </Link>
          <span>/</span>
          <Link to="/news" className="hover:text-[#3d4f45]">
            Berita
          </Link>
          <span>/</span>
          <span className="text-[#3d4f45] truncate max-w-xs">
            {news.title || "Detail Berita"}
          </span>
        </div>
      </section>

      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="article-body">
            {(news.body || news.summary || "Isi berita belum tersedia.")
              .split("\n\n")
              .map((para, i) => (
                <p key={i}>{para}</p>
              ))}
          </div>

          <div className="mt-12 pt-8 border-t border-gray-100">
            <h3 className="font-semibold mb-4 text-[#3d4f45] text-sm">
              Bagikan Artikel
            </h3>

            <div className="flex gap-3 flex-wrap">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(
                  (news.title || "Berita") + " " + window.location.href,
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25D366] text-white px-5 py-2 rounded-full text-xs hover:opacity-90 transition"
              >
                Share via WhatsApp
              </a>

              <button
                onClick={handleCopyLink}
                className="border border-[#3d4f45] text-[#3d4f45] px-5 py-2 rounded-full text-xs hover:bg-[#3d4f45] hover:text-white transition"
              >
                Salin Link
              </button>

              <button
                onClick={() => navigate("/news")}
                className="border border-gray-300 text-gray-600 px-5 py-2 rounded-full text-xs hover:bg-gray-100 transition"
              >
                ← Kembali ke Berita
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 bg-[#f5f7f6]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl text-center mb-10 text-[#3d4f45] font-light">
            Berita Terkait
          </h2>

          {relatedNews.length === 0 ? (
            <p className="text-center text-gray-500">
              Belum ada berita terkait.
            </p>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {relatedNews.map((n) => (
                <div
                  key={n.id}
                  className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition cursor-pointer card-wrap-hover"
                  onClick={() => {
                    navigate(`/news/${n.slug || n.id}`);
                    window.scrollTo(0, 0);
                  }}
                >
                  <div className="h-48 overflow-hidden bg-[#b8c5ba] flex items-center justify-center">
                    {n.cover_url ? (
                      <img
                        src={n.cover_url}
                        alt={n.title}
                        className="card-img w-full h-full object-cover"
                        onError={(e) => (e.target.style.display = "none")}
                      />
                    ) : (
                      <span className="text-gray-500 text-sm">
                        Foto belum tersedia
                      </span>
                    )}
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-xs bg-[#f0f4f1] text-[#3d4f45] px-2 py-0.5 rounded-full">
                        Berita
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatTanggalIndonesia(n.published_at)}
                      </span>
                    </div>

                    <h3 className="text-sm font-semibold text-[#3d4f45] leading-snug mb-2 line-clamp-2">
                      {n.title || "Tanpa Judul"}
                    </h3>

                    <span className="text-xs text-[#3d4f45] hover:underline">
                      Baca Selengkapnya →
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {isAdmin && (
        <div className="max-w-6xl mx-auto px-6 pb-8">
          <hr className="my-8 border-gray-200" />
          <AdminSection />
        </div>
      )}

      <Footer />
    </div>
  );
}
